import * as React from 'react';

import {Session} from 'nutshell-core/session';

import {CheckmarkButton} from '../button/checkmark-button/checkmark-button';
import {CloseButton} from '../button/close-button/close-button';
import {IconButton} from '../button/icon-button/icon-button';
import {PencilOutlineIcon} from '../icon/svg/pencil-outline-icon';
import {Stack} from '../layout/stack/stack';
import {Body} from '../typography/body';

import {FormattedNumberField} from './formatted-number-field';

import styles from './editable-currency-field.module.css';

type Props = {
    value: number;
    currencyCode: string | null | undefined;
    onSubmit: (arg1: number) => void;
    isEditing: boolean;
    setIsEditing: (arg1: boolean) => void;
    isSmall?: boolean;
    shouldSubmitOnBlur?: boolean;
};

export function EditableCurrencyField(props: Props) {
    const {onSubmit, isEditing, setIsEditing} = props;

    const [value, setValue] = React.useState<number>(props.value);

    React.useEffect(() => {
        setValue(props.value);
    }, [props.value]);

    const currencyCode = React.useMemo(() => {
        return props.currencyCode ?? Session.getInstanceDefaultCurrencyCode();
    }, [props.currencyCode]);

    const handleChange = React.useCallback((newValue?: number | null) => {
        setValue(newValue ?? 0);
    }, []);

    const handleSubmit = React.useCallback(() => {
        setIsEditing(false);
        if (value === props.value) {
            return;
        }

        onSubmit(value ?? 0);
    }, [value, props.value, onSubmit, setIsEditing]);

    const handleCancel = React.useCallback(() => {
        setIsEditing(false);
        setValue(props.value);
    }, [props.value, setIsEditing]);

    const formattedValue = React.useMemo(() => {
        return value.toLocaleString(undefined, {
            style: 'currency',
            currency: currencyCode,
        });
    }, [value, currencyCode]);

    return (
        <div onClick={(e) => e.stopPropagation()} className={styles['editable-currency-field']}>
            {isEditing ? (
                <Stack spacing={8} isFullWidth={true}>
                    <Stack isHorizontal={true} isFullWidth={true} spacing={8} alignItems='center'>
                        <FormattedNumberField
                            value={value.toFixed(2)}
                            onEnter={handleSubmit}
                            onEscape={handleCancel}
                            onChange={handleChange}
                            onBlur={props.shouldSubmitOnBlur ? handleSubmit : undefined}
                            formatType='currency'
                            currencyCode={currencyCode}
                            autoFocus={true}
                            isBold={true}
                            fontSize={props.isSmall ? 17 : 21}
                        />
                        <CheckmarkButton onClick={handleSubmit} />
                        <CloseButton onPointerDown={handleCancel} alignCenter={true} size={16} />
                    </Stack>
                </Stack>
            ) : (
                <div
                    className='pointer-cursor flex justify-end truncate'
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsEditing(true);
                    }}
                >
                    <Stack isHorizontal={true} spacing={8} alignItems='center'>
                        <Body
                            isBold={true}
                            size={props.isSmall ? 'large' : 'xlarge'}
                            textAlign='right'
                        >
                            {formattedValue}
                        </Body>
                        <div className={styles['edit-icon-button']}>
                            <IconButton
                                icon={PencilOutlineIcon}
                                tooltipText='Edit value'
                                noPadding={true}
                                onClick={() => setIsEditing(true)}
                            />
                        </div>
                    </Stack>
                </div>
            )}
        </div>
    );
}
